import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const getUserImageUrl = (value) => {
  if (!value) return "";
  if (String(value).startsWith("http")) return value;
  return `${Constants.imageDomain}${value}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const formatLastActive = (value) => {
  if (!value) return "No activity date available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No activity date available";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function ActiveUserIdentity({ user }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const name = String(user?.name || "Unknown User").trim() || "Unknown User";
  const imageUrl = getUserImageUrl(user?.profile_pic_url);
  const hasImage = Boolean(imageUrl) && !imageFailed;
  const detailsUrl = `/user-details/${user?._id}`;

  return (
    <>
      <MDBox display="flex" alignItems="center" gap={1.4} sx={{ minWidth: 245 }}>
        {hasImage ? (
          <MDBox
            component="button"
            type="button"
            aria-label={`Enlarge ${name}'s profile picture`}
            onClick={() => setPreviewOpen(true)}
            sx={{
              width: 48,
              height: 48,
              p: 0,
              border: "2px solid rgba(85, 214, 142, 0.42)",
              borderRadius: "50%",
              overflow: "hidden",
              background: "transparent",
              cursor: "zoom-in",
              flexShrink: 0,
              transition: "transform 160ms ease, border-color 160ms ease",
              "&:hover": {
                transform: "scale(1.06)",
                borderColor: "#55d68e",
              },
              "&:focus-visible": {
                outline: "2px solid #55d68e",
                outlineOffset: 2,
              },
            }}
          >
            <MDBox
              component="img"
              src={imageUrl}
              alt={name}
              onError={() => setImageFailed(true)}
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </MDBox>
        ) : (
          <MDBox
            component={Link}
            to={detailsUrl}
            width={48}
            height={48}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="50%"
            sx={{
              color: "#ffffff",
              fontWeight: 800,
              textDecoration: "none",
              flexShrink: 0,
              background: "linear-gradient(135deg, #1f9d61 0%, #f27500 100%)",
            }}
          >
            {name.charAt(0).toUpperCase()}
          </MDBox>
        )}

        <MDBox>
          <MDTypography
            component={Link}
            to={detailsUrl}
            variant="button"
            fontWeight="bold"
            sx={{
              color: "#ffffff",
              textDecoration: "none",
              lineHeight: 1.25,
              "&:hover": { color: "#55d68e" },
            }}
          >
            {name}
          </MDTypography>
          <MDTypography
            variant="caption"
            display="block"
            sx={{ mt: 0.4, color: "#8fa7c4", whiteSpace: "nowrap" }}
          >
            Last active: {formatLastActive(user?.last_active_at)}
          </MDTypography>
        </MDBox>
      </MDBox>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            color: "#ffffff",
            borderRadius: "18px",
            border: "1px solid rgba(85, 214, 142, 0.24)",
            background: "linear-gradient(180deg, #0d1d2f 0%, #08131f 100%)",
            boxShadow: "0 28px 90px rgba(0, 0, 0, 0.58)",
          },
        }}
      >
        <DialogTitle sx={{ pr: 7, color: "#ffffff", fontWeight: 700 }}>
          {name}
          <IconButton
            aria-label="Close profile picture"
            onClick={() => setPreviewOpen(false)}
            sx={{ position: "absolute", top: 10, right: 10, color: "#c7d8eb" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <MDBox
            component="img"
            src={imageUrl}
            alt={`${name} profile`}
            sx={{
              display: "block",
              width: "100%",
              maxHeight: "65vh",
              objectFit: "contain",
              borderRadius: "12px",
              backgroundColor: "#06101a",
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setPreviewOpen(false)}>
            Close
          </MDButton>
          <MDButton component={Link} to={detailsUrl} variant="gradient" color="success">
            View User Details
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

ActiveUserIdentity.propTypes = {
  user: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    name: PropTypes.string,
    profile_pic_url: PropTypes.string,
    last_active_at: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }).isRequired,
};

export default function ActiveUsers() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalDocs, setTotalDocs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "" });

  const loadActiveUsers = async ({
    nextPage = page,
    nextLimit = limit,
    nextSearch = appliedSearch,
  } = {}) => {
    if (init() !== "success") return;

    setLoading(true);
    try {
      const response = await axios.get(Constants.getUrls.getMostActiveUsers, {
        params: {
          page: nextPage,
          limit: nextLimit,
          search: nextSearch,
        },
      });
      const payload = response?.data?.data || {};
      setUsers(Array.isArray(payload.docs) ? payload.docs : []);
      setPage(Number(payload.page || nextPage));
      setLimit(Number(payload.limit || nextLimit));
      setTotalDocs(Number(payload.totalDocs || 0));
      setTotalPages(Number(payload.totalPages || 1));
    } catch (error) {
      setUsers([]);
      setNotification({
        open: true,
        message: error?.response?.data?.message || "Unable to load active users.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActiveUsers({ nextPage: 1, nextLimit: 25, nextSearch: "" });
    // The first request intentionally runs only when this page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySearch = () => {
    const nextSearch = search.trim();
    setAppliedSearch(nextSearch);
    loadActiveUsers({ nextPage: 1, nextSearch });
  };

  const clearSearch = () => {
    setSearch("");
    setAppliedSearch("");
    loadActiveUsers({ nextPage: 1, nextSearch: "" });
  };

  const rankCell = (rank) => {
    const rankColors = {
      1: { background: "rgba(245, 188, 66, 0.18)", color: "#ffd56a" },
      2: { background: "rgba(190, 205, 220, 0.16)", color: "#dce8f5" },
      3: { background: "rgba(205, 127, 50, 0.18)", color: "#e9a96a" },
    };
    const tone = rankColors[rank] || {
      background: "rgba(85, 214, 142, 0.1)",
      color: "#8fe7b6",
    };

    return (
      <MDBox
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        minWidth="36px"
        px={1}
        py={0.6}
        borderRadius="10px"
        sx={{ ...tone, fontSize: "0.78rem", fontWeight: 800 }}
      >
        #{rank}
      </MDBox>
    );
  };

  const tableRows = users.map((user) => ({
    rank: rankCell(Number(user?.rank || 0)),
    user: <ActiveUserIdentity user={user} />,
    mcq_solved: formatNumber(user?.mcq_solved),
    total_points: formatNumber(user?.total_points),
    practice_test_count: formatNumber(user?.practice_test_count),
    ar_count: formatNumber(user?.ar_count),
    agriboost_card_count: formatNumber(user?.agriboost_card_count),
    pyq_count: formatNumber(user?.pyq_count),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox
          mb={3}
          p={{ xs: 2.25, md: 3 }}
          borderRadius="18px"
          sx={{
            border: "1px solid rgba(85, 214, 142, 0.18)",
            background:
              "radial-gradient(circle at 92% 12%, rgba(31,157,97,0.2), transparent 32%), linear-gradient(135deg, #10243a 0%, #091522 72%)",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDBox
              width="46px"
              height="46px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="13px"
              sx={{ color: "#ffffff", background: "linear-gradient(135deg, #28b76f, #147a49)" }}
            >
              <Icon>leaderboard</Icon>
            </MDBox>
            <MDBox>
              <MDTypography variant="h4" color="white">
                Active Users
              </MDTypography>
              <MDTypography variant="button" sx={{ color: "#9db4cf" }}>
                Active means a submitted practice test, an AgriBoost card view, or PYQ/AR
                activity. Users are ranked by the displayed counts; points and the latest
                qualifying date resolve ties.
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox
          borderRadius="18px"
          sx={{
            overflow: "hidden",
            border: "1px solid rgba(126, 170, 220, 0.14)",
            background: "linear-gradient(180deg, rgba(10,20,34,0.98), rgba(7,15,26,0.98))",
          }}
        >
          <MDBox px={{ xs: 2, md: 2.5 }} py={2.25}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7} lg={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by user name, mobile or email"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") applySearch();
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5} lg={6}>
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <MDButton color="info" variant="gradient" onClick={applySearch}>
                    Search
                  </MDButton>
                  <MDButton color="secondary" variant="outlined" onClick={clearSearch}>
                    Clear
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          {loading ? (
            <MDBox py={7} textAlign="center">
              <MDTypography variant="button" sx={{ color: "#b8cbe0" }}>
                Loading active users...
              </MDTypography>
            </MDBox>
          ) : users.length ? (
            <DataTable
              entriesPerPage={{ defaultValue: limit, entries: [10, 25, 50, 100] }}
              showTotalEntries
              isSorted={false}
              table={{
                columns: [
                  { Header: "Rank", accessor: "rank", width: "7%" },
                  { Header: "User", accessor: "user", width: "30%" },
                  { Header: "MCQ Solved", accessor: "mcq_solved", align: "center" },
                  { Header: "Total Points", accessor: "total_points", align: "center" },
                  {
                    Header: "Practice Tests",
                    accessor: "practice_test_count",
                    align: "center",
                  },
                  { Header: "AR Count", accessor: "ar_count", align: "center" },
                  {
                    Header: "AgriBoost Cards",
                    accessor: "agriboost_card_count",
                    align: "center",
                  },
                  { Header: "PYQ Count", accessor: "pyq_count", align: "center" },
                ],
                rows: tableRows,
              }}
              page_no={page}
              totalPages={totalPages}
              totalDocs={totalDocs}
              onPageChange={(nextPage) => loadActiveUsers({ nextPage })}
              onEntriesPerPageChange={(nextLimit) =>
                loadActiveUsers({ nextPage: 1, nextLimit })
              }
            />
          ) : (
            <MDBox px={2.5} py={6} textAlign="center">
              <MDTypography variant="h6" color="white">
                No active users found
              </MDTypography>
              <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
                {appliedSearch
                  ? "Try another name, mobile number, or email address."
                  : "Activity will appear here after users start using the app."}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <MDSnackbar
        color="error"
        icon="warning"
        title="AgriQuiz"
        content={notification.message}
        dateTime="Just now"
        open={notification.open}
        close={() => setNotification({ open: false, message: "" })}
      />
    </DashboardLayout>
  );
}
